import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
  when,
} from 'mobx'
import sortBy from 'lodash/sortBy'
import type { Cover } from 'components/PhotoBook/PhotoBookProvider/PhotoBook.types'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { updateAmplitudeUserProperties } from 'services/Analytics/platforms/amplitude'
import { logger } from 'services/logger'
import StorageManager from 'services/StorageManager'
import { indexedDbPhotoBookBackend } from 'services/PhotoBook/photoBookDb'
import { reportPhotoBookDbFailure } from 'services/PhotoBook/photoBookDbFailure'
import { localStoragePhotoBookBackend } from 'services/PhotoBook/photoBookLocalStorage'
import {
  moveLegacyPhotoBookIntoBackend,
  readAllStoredPhotoBooks,
} from 'services/PhotoBook/photoBookStorage.utils'
import type { PhotoBookStorageBackend } from 'services/PhotoBook/photoBookStorageBackend.types'
import {
  isBrowserMarkedForLocalStoragePhotoBooks,
  markBrowserForLocalStoragePhotoBooks,
  type PhotoBookLocalStorageFallbackReason,
} from 'services/PhotoBook/photoBookStorageBackendMark'
import type { StoredPhotoBook } from 'services/PhotoBook/photoBookSaver.types'
import { mintPhotoBookId } from 'services/PhotoBook/photoBookId'
import { isClient } from 'utils/runtimeUtils'
import {
  readMobxSnapshot,
  readOutsideReaction,
} from 'utils/mobx/readOutsideReaction'
import type { PhotoBankStore } from '../photoBankStore/PhotoBankStore'
import { PhotoBookDataStore } from '../photoBookDataStore/PhotoBookDataStore'
import type { PhotoBookDraftSyncDeps } from '../photoBookDataStore/PhotoBookDraftSyncer'
import { PhotoBooksRemoteSyncer } from './PhotoBooksRemoteSyncer'

export const EXTERNAL_BOOK_SOURCES = [
  'Account Draft',
  'User Cart',
  'Reorder',
  'Switch Browser',
] as const

export type ExternalBookSource = (typeof EXTERNAL_BOOK_SOURCES)[number]

export const LAST_VIEWED_PHOTO_BOOK_ID_STORAGE_KEY = 'lastViewedPhotoBookId'

type ExternalBookOutcome =
  | 'Created'
  | 'Create Failed'
  | 'Replaced Local'
  | 'Kept Local'
  | 'Dropped While Editing'

export type BookArrival = {
  source: ExternalBookSource
  addedBookIds: string[]
}

type HeldBookStore = PhotoBookDataStore & { bookData: StoredPhotoBook }

type PhotoBooksLoadStep =
  | 'move-legacy-slot'
  | 'read-indexed-db'
  | 'read-local-storage'
  | 'build-book-stores'

type PhotoBookLocalStorageFallback =
  | { reason: 'no-indexeddb' }
  | {
      reason: Exclude<PhotoBookLocalStorageFallbackReason, 'no-indexeddb'>
      errorName: string
    }

// Only ever called for a browser not marked yet, so each browser tracks its
// flip once.
function markBrowserForLocalStoragePhotoBooksAndTrack(
  fallback: PhotoBookLocalStorageFallback
): void {
  markBrowserForLocalStoragePhotoBooks()
  analytics.track(EVENT_NAMES.BOOKS_STORAGE_FELL_BACK_TO_LOCAL_STORAGE, {
    Reason: fallback.reason,
    ...('errorName' in fallback ? { 'Error Name': fallback.errorName } : null),
  })
  // The load runs before analytics.init(), and analytics.setUserProperties
  // throws until init sets its Braze instance. Amplitude queues pre-init calls.
  updateAmplitudeUserProperties({
    set: { 'Photo Book Storage': 'local-storage' },
  })
}

function getReadLoadStep(
  storageBackend: PhotoBookStorageBackend
): PhotoBooksLoadStep {
  return storageBackend === indexedDbPhotoBookBackend
    ? 'read-indexed-db'
    : 'read-local-storage'
}

function pickInitialPhotoBookStorageBackend(): PhotoBookStorageBackend {
  if (isBrowserMarkedForLocalStoragePhotoBooks()) {
    return localStoragePhotoBookBackend
  }
  if (typeof indexedDB === 'undefined') {
    markBrowserForLocalStoragePhotoBooksAndTrack({ reason: 'no-indexeddb' })
    return localStoragePhotoBookBackend
  }
  return indexedDbPhotoBookBackend
}

// Whatever a past build or another tab left in the slot: only a string can name
// a book, and an id naming no book is handled downstream.
function readLastViewedBookId(): string | null {
  const storedBookId = StorageManager.get<unknown>(
    LAST_VIEWED_PHOTO_BOOK_ID_STORAGE_KEY
  )
  return typeof storedBookId === 'string' ? storedBookId : null
}

/**
 * Owns one PhotoBookDataStore per book this browser holds, keyed by book id.
 *
 * The storage backend is the index: whatever it holds is the set of books, so
 * seeding reads it rather than a second persisted index that could disagree
 * with it. The backend is IndexedDB, or localStorage for a browser whose
 * IndexedDB is missing or failed once.
 */
export class PhotoBooksStore {
  // The parent owns the user-cart channel — one slot replaced wholesale, so two
  // writers of it would let a single-book change wipe the other books' remote
  // copies — and the order draft's plumbing: the write-ordering queue, the
  // saver, and the account gate. Each book queues its own draft entry through
  // the callbacks below, and entries ready together share one request.
  readonly remoteSyncer: PhotoBooksRemoteSyncer

  private readonly draftSyncDeps: PhotoBookDraftSyncDeps

  // The backend the load ended on, resolved before isLoaded flips. A book
  // created or received while the load is still running writes through it, so
  // it lands in the store the next page will read.
  private readonly storageBackendPromise: Promise<PhotoBookStorageBackend>

  private resolveStorageBackend!: (
    storageBackend: PhotoBookStorageBackend
  ) => void

  @observable.shallow accessor booksMap = new Map<string, PhotoBookDataStore>()

  // The storage read is async, so the map is empty until the load
  // resolves. Consumers that would treat "not loaded yet" as "no books" —
  // the cart pruning rows, the editor route resolving an id — wait on this.
  @observable accessor isLoaded = false

  // The account request has come back and its books have been merged in. Until
  // then, nothing may reach the order draft: this browser would push its books
  // over ones it has never seen.
  @observable accessor hasMergedAccountBooks = false

  // The book this browser last had on screen, which is the one a bare editor
  // URL reopens. Kept per browser rather than on the book itself: a view is not
  // an edit, and the book opened on a phone must not decide what the desktop
  // opens. Survives the book it names — see bookToShow.
  @observable accessor lastViewedBookId: string | null = null

  // Books this browser had never seen, waiting to be announced to the user.
  // Deliberately not persisted: a tab that closes before the notice shows has
  // nothing left to say.
  @observable.ref accessor pendingBookArrival: BookArrival | null = null

  // Bumped by every path that unbinds this browser's books from the account
  // they came from. Installing a book is a chain of awaits, the storage load
  // and then the write, so a restore that started before a sign-out would
  // otherwise write the previous account's book back after the wipe and hand it
  // to whoever signs in next. Deliberately unobservable: the async scopes that
  // read it are exactly where an observable read would warn.
  private booksSessionGeneration = 0

  constructor(photoBankStore: PhotoBankStore) {
    this.remoteSyncer = new PhotoBooksRemoteSyncer(photoBankStore, {
      getBooksToSync: () => this.booksToSync,
      getIsAnyBookWithheldFromSync: () => this.isAnyBookWithheldFromSync,
      getIsAccountSyncReady: () => this.hasMergedAccountBooks,
    })
    this.draftSyncDeps = {
      isDraftPushReady: () => this.remoteSyncer.isDraftPushReady(),
      isBookPushable: (book) => this.remoteSyncer.isBookPushable(book),
      pushBookToDraft: (book) => this.remoteSyncer.pushBookToDraft(book),
    }
    this.storageBackendPromise = new Promise((resolve) => {
      this.resolveStorageBackend = resolve
    })

    // Storage is only readable on the client; a server-rendered store holds no
    // books until the client one loads itself.
    if (isClient()) {
      this.lastViewedBookId = readLastViewedBookId()
      // Fire-and-forget: the load reports through isLoaded and logs failures.
      this.loadStoredBooks()
      this.remoteSyncer.startSyncing()
    }
    makeObservable(this)
  }

  // Hands out a store for an unknown id too, with no book in it — the state an
  // editor opens in before the first upload. Nothing is persisted until a book
  // is created in it, so an abandoned empty store costs only memory.
  @action
  getOrCreateBook(bookId: string): PhotoBookDataStore {
    const existingBookStore = this.booksMap.get(bookId)
    if (existingBookStore) {
      return existingBookStore
    }

    const bookStore = new PhotoBookDataStore(
      bookId,
      this.storageBackendPromise,
      null,
      this.draftSyncDeps
    )
    this.booksMap.set(bookId, bookStore)
    return bookStore
  }

  // Resolves null when the seed write did not land (the origin quota): the
  // book is rolled out of the map rather than living in memory only and
  // vanishing on reload. The caller owns telling the user.
  async createBook(
    cover: Cover,
    bookId: string = mintPhotoBookId()
  ): Promise<PhotoBookDataStore | null> {
    const bookStore = this.getOrCreateBook(bookId)
    const wasWritten = await bookStore.createBook(cover)
    if (!wasWritten) {
      logger.error('Photo book creation write did not land', null, { bookId })
      this.rollBookOutOfMap(bookId)
      return null
    }

    return bookStore
  }

  /**
   * Every book arriving from outside this browser — the account's draft, a
   * user cart restore, a reorder, a switch-browser handover — lands here.
   * Books are never compared: a matching id is the same book (the fresher edit
   * wins), any other id is a new book with its own cart row. An id-less legacy
   * payload arrives under the one fixed legacy id, so every channel carrying
   * that book resolves to the same one; only a reorder mints, being a copy.
   */
  async receiveExternalBook(
    book: StoredPhotoBook,
    source: ExternalBookSource
  ): Promise<void> {
    const sessionGeneration = this.booksSessionGeneration

    // Matching by id needs the local set: merging before the load answers
    // would duplicate a stored book instead of updating it.
    await when(() => this.isLoaded)
    if (this.booksSessionGeneration !== sessionGeneration) return

    // A clock ahead of ours must not pin its book as permanently newest.
    const receivedBook = {
      ...book,
      lastEditedAt: Math.min(book.lastEditedAt, Date.now()),
    }
    const outcome = await this.mergeExternalBook(receivedBook)

    // A write already in flight when the wipe ran is undone by the deletion the
    // wipe queued after it, so this book is gone from storage either way, and
    // announcing it would name a book the user no longer has.
    if (this.booksSessionGeneration !== sessionGeneration) return

    // The order-draft round trip is lossy for client-only photo fields, so the
    // edit stamp is the version boundary. Seed with the merged local shape so
    // those fields do not look like a new edit when the gate opens.
    const mergedBookStore = readOutsideReaction(() =>
      this.booksMap.get(receivedBook.bookId)
    )
    if (source === 'Account Draft' && mergedBookStore) {
      const mergedBook = readMobxSnapshot(() => mergedBookStore.bookData)
      if (mergedBook?.lastEditedAt === receivedBook.lastEditedAt) {
        mergedBookStore.markBookAlreadyInDraft(mergedBook)
      }
    }

    if (outcome === 'Created') {
      this.recordBookArrival(source, receivedBook.bookId)
    }

    // With no experiment gating this path, this event is the only early
    // warning that merges misbehave in the field.
    analytics.track(EVENT_NAMES.BOOKS_EXTERNAL_BOOK_RECEIVED, {
      'Book ID': receivedBook.bookId,
      Source: source,
      Outcome: outcome,
      'Was Book Added': outcome === 'Created',
    })
  }

  private async mergeExternalBook(
    receivedBook: StoredPhotoBook
  ): Promise<ExternalBookOutcome> {
    // The stamp rather than a snapshot of the book: comparing one number must
    // not clone every page of it.
    const localBook = readOutsideReaction(() => {
      const bookStore = this.booksMap.get(receivedBook.bookId)
      const bookData = bookStore?.bookData
      return bookStore && bookData
        ? {
            bookStore,
            isEditorMounted: bookStore.isEditorMounted,
            lastEditedAt: bookData.lastEditedAt,
          }
        : null
    })

    if (localBook) {
      // An ordering guard, not a comparison: while the user is editing this
      // book, local wins — the syncer pushes the local state anyway.
      if (localBook.isEditorMounted) {
        return 'Dropped While Editing'
      }
      if (receivedBook.lastEditedAt <= localBook.lastEditedAt) {
        return 'Kept Local'
      }
      localBook.bookStore.replaceBook(receivedBook)
      return 'Replaced Local'
    }

    const bookStore = this.getOrCreateBook(receivedBook.bookId)
    const wasWritten = await bookStore.replaceBook(receivedBook)
    if (!wasWritten) {
      logger.error('Received photo book write did not land', null, {
        bookId: receivedBook.bookId,
      })
      this.rollBookOutOfMap(receivedBook.bookId)
      return 'Create Failed'
    }

    return 'Created'
  }

  // One restore is one notice, so books arriving together accumulate. A
  // different source starts a fresh arrival instead: the notice's wording is
  // source-specific, and one source cannot speak for the other's books.
  @action
  private recordBookArrival(source: ExternalBookSource, bookId: string): void {
    const pendingArrival = this.pendingBookArrival
    this.pendingBookArrival =
      pendingArrival?.source === source
        ? { source, addedBookIds: [...pendingArrival.addedBookIds, bookId] }
        : { source, addedBookIds: [bookId] }
  }

  @action
  takePendingBookArrival(): BookArrival | null {
    const arrival = this.pendingBookArrival
    this.pendingBookArrival = null
    return arrival
  }

  // The account's books, merged before the gate opens: opening it earlier
  // would let the draft push run over account books not yet merged in.
  async onAccountBooksLoaded(
    accountBooks: StoredPhotoBook[],
    deletedBookIds: string[]
  ): Promise<void> {
    const sessionGeneration = this.booksSessionGeneration

    // Deleting needs the local set: a tombstoned book still loading from
    // storage would slip past the deletion and come back.
    await when(() => this.isLoaded)
    if (this.booksSessionGeneration !== sessionGeneration) return

    // A book deleted on another device is gone from the account for good — the
    // server refuses it from then on — so a copy still here could never sync
    // again. Dropping it is what the tombstone is for.
    for (const deletedBookId of deletedBookIds) {
      this.deleteBook(deletedBookId)
    }
    for (const accountBook of accountBooks) {
      await this.receiveExternalBook(accountBook, 'Account Draft')
      // Every remaining book belongs to an account this browser has left, and
      // the gate below would reopen the draft push for a session without one.
      if (this.booksSessionGeneration !== sessionGeneration) return
    }
    runInAction(() => {
      this.hasMergedAccountBooks = true
    })
  }

  // Closes the draft gate and forgets what each book pushed: a baseline built
  // under one account must not suppress the first push into the next one.
  // Signing out clears the books themselves through clearAllBooks, and a
  // session that was never authenticated keeps its books.
  @action
  onSignedOut(): void {
    this.booksSessionGeneration += 1
    this.hasMergedAccountBooks = false
    this.pendingBookArrival = null
    this.remoteSyncer.cancelPendingDraftUpserts()
    for (const bookStore of this.booksMap.values()) {
      bookStore.resetDraftBaseline()
    }
  }

  // Every abandoned editor visit hands back the empty store getOrCreateBook minted for
  // it, or the map fills with phantom books that were never created.
  @action
  releaseBookIfEmpty(bookId: string): void {
    const bookStore = this.booksMap.get(bookId)
    if (!bookStore || bookStore.bookData !== null) {
      return
    }

    this.rollBookOutOfMap(bookId)
  }

  // The single cross-layer delete: memory, storage, and both remote channels
  // — the user cart gets the surviving books, or emptiness when none
  // remain; the order draft gets the deletion by id. The cart holds rows whose
  // book may already be gone, so an unknown id is a no-op rather than an error.
  @action
  deleteBook(bookId: string): void {
    const bookStore = this.booksMap.get(bookId)
    if (!bookStore) {
      return
    }

    // An empty store — the one an editor visit opens in — was never written
    // anywhere, so releasing it is the whole deletion. Naming it to the account
    // would tombstone an id for good that no book ever used.
    if (bookStore.bookData === null) {
      this.rollBookOutOfMap(bookId)
      return
    }

    bookStore.clearBook()
    this.rollBookOutOfMap(bookId)
    this.remoteSyncer.pushBooksAfterDelete(bookId)
  }

  // Logout clears this browser, not the account: the draft stays for the next
  // sign-in, so no remote push happens here.
  @action
  clearAllBooks(): void {
    this.booksSessionGeneration += 1
    this.pendingBookArrival = null
    // Whoever signs in next opens their own first book, not an id from the
    // books this wipe is taking away.
    this.lastViewedBookId = null
    StorageManager.remove(LAST_VIEWED_PHOTO_BOOK_ID_STORAGE_KEY)
    this.remoteSyncer.cancelPendingDraftUpserts()
    for (const bookStore of this.booksMap.values()) {
      // Freeze first: the photo-bank clearing around logout re-fires the
      // persistence reactions, which would re-write the book being removed.
      bookStore.setIsEditorInitializing(true)
      bookStore.clearBook()
      bookStore.dispose()
    }
    this.booksMap.clear()
  }

  // Only stores holding a book count: getOrCreateBook hands out empty stores for any
  // id it is asked about, and those must not read as a book in progress.
  @computed
  get hasAnyBook(): boolean {
    return this.books.length > 0
  }

  @computed
  get books(): StoredPhotoBook[] {
    return Array.from(this.booksMap.values()).flatMap((bookStore) =>
      bookStore.bookData ? [bookStore.bookData] : []
    )
  }

  // A computed over the flag alone, so a reader of it does not re-render on
  // every edit the way a selector over `books` would.
  @computed
  get isAnyEditorReady(): boolean {
    return Array.from(this.booksMap.values()).some(
      (bookStore) => bookStore.isEditorReady
    )
  }

  // The order the books are shown in, the bag's rows included. Derived from each
  // book's own sortKey rather than booksMap fill order, which follows whatever
  // order IndexedDB and the merges happened to hand the books over in.
  @computed
  get bookStoresInDisplayOrder(): HeldBookStore[] {
    const heldBookStores = Array.from(this.booksMap.values()).filter(
      (bookStore): bookStore is HeldBookStore => bookStore.bookData !== null
    )
    return sortBy(heldBookStores, [
      (bookStore) => bookStore.bookData.sortKey,
      (bookStore) => bookStore.bookId,
    ])
  }

  /**
   * The one book every single-book surface speaks about: what a bare editor URL
   * opens, what the single-product checkout orders, what its summary describes.
   * They share this computed because a divergence would order a different book
   * than the editor was showing.
   *
   * The last book the user viewed, and before any view — or after the viewed
   * book is gone, deleted here or in another tab — the first book, which is the
   * order the cart lists its book rows in. Null is the empty editor.
   */
  @computed
  get bookToShow(): PhotoBookDataStore | null {
    const lastViewedBookStore = this.lastViewedBookId
      ? this.booksMap.get(this.lastViewedBookId)
      : undefined
    if (lastViewedBookStore?.bookData) {
      return lastViewedBookStore
    }

    return this.bookStoresInDisplayOrder[0] ?? null
  }

  // The editor route reports the book it put on screen. Written through on
  // every view rather than on unmount: a tab closed in the editor still has to
  // reopen that book next time.
  @action
  markBookViewed(bookId: string): void {
    this.lastViewedBookId = bookId
    StorageManager.set(LAST_VIEWED_PHOTO_BOOK_ID_STORAGE_KEY, bookId)
  }

  // Tests build a store per case and must release the children's autoruns, or
  // one case's stores keep writing into the next case's storage.
  dispose(): void {
    const bookStores = readOutsideReaction(() => [...this.booksMap.values()])
    for (const bookStore of bookStores) {
      bookStore.dispose()
    }
    this.remoteSyncer.dispose()
  }

  @computed
  private get booksToSync(): StoredPhotoBook[] {
    return Array.from(this.booksMap.values()).flatMap((bookStore) =>
      bookStore.bookToPersist ? [bookStore.bookToPersist] : []
    )
  }

  // The user cart is one slot replaced wholesale, so booksToSync leaving a
  // withheld book out would erase that book remotely for as long as the editor
  // holds it — a cover edit lasts as long as the user is in it. The whole push
  // waits for the book instead.
  @computed
  private get isAnyBookWithheldFromSync(): boolean {
    return Array.from(this.booksMap.values()).some(
      (bookStore) => bookStore.isBookWithheldFromSync
    )
  }

  @action
  private rollBookOutOfMap(bookId: string): void {
    const bookStore = this.booksMap.get(bookId)
    if (!bookStore) {
      return
    }

    bookStore.dispose()
    this.booksMap.delete(bookId)
  }

  // The read is the only IndexedDB step under a timeout, so it runs before the
  // legacy move: a move first would wait on a hung open with no timer running,
  // and the browser would never get marked. The move then lands the legacy book
  // in whichever backend the load settled on.
  private async loadStoredBooks(): Promise<void> {
    let storageBackend = pickInitialPhotoBookStorageBackend()
    let loadStep = getReadLoadStep(storageBackend)
    try {
      let storedBooks: StoredPhotoBook[]
      try {
        storedBooks = await readAllStoredPhotoBooks(storageBackend)
      } catch (err) {
        if (storageBackend !== indexedDbPhotoBookBackend) {
          throw err
        }
        const failure = reportPhotoBookDbFailure('load', err, {
          loadStep,
          storageBackend: storageBackend.name,
        })
        // A tab the browser is unloading aborts healthy IndexedDB reads too,
        // and marking on it would move a working browser off IndexedDB for good.
        if (failure.failureKind === 'page-unloading') {
          return
        }

        markBrowserForLocalStoragePhotoBooksAndTrack({
          reason:
            failure.failureKind === 'timed-out'
              ? 'indexeddb-timed-out'
              : 'indexeddb-failed',
          errorName: failure.errorName,
        })
        storageBackend = localStoragePhotoBookBackend
        loadStep = getReadLoadStep(storageBackend)
        storedBooks = await readAllStoredPhotoBooks(storageBackend)
      }

      loadStep = 'move-legacy-slot'
      const wasLegacyBookMoved =
        await moveLegacyPhotoBookIntoBackend(storageBackend)
      if (wasLegacyBookMoved) {
        loadStep = getReadLoadStep(storageBackend)
        storedBooks = await readAllStoredPhotoBooks(storageBackend)
      }

      loadStep = 'build-book-stores'
      runInAction(() => {
        for (const storedBook of storedBooks) {
          this.booksMap.set(
            storedBook.bookId,
            new PhotoBookDataStore(
              storedBook.bookId,
              this.storageBackendPromise,
              storedBook,
              this.draftSyncDeps
            )
          )
        }
      })
    } catch (err) {
      reportPhotoBookDbFailure('load', err, {
        loadStep,
        storageBackend: storageBackend.name,
      })
    } finally {
      this.resolveStorageBackend(storageBackend)
      // Flips even when the load failed: a store stuck at not-loaded would
      // freeze the cart's book rows and the editor's splash forever.
      runInAction(() => {
        this.isLoaded = true
      })
    }
  }
}
